// B2bCalls.jsx — adapted from SeasonalCalls.jsx for b2b calls

import React, { useMemo, useState } from "react";
import { Container, Table, Button, Form, Modal, Row, Col, Badge, Tabs, Tab, Alert, Card } from "react-bootstrap";
import { FaPlus, FaPhone, FaEdit, FaSpinner, FaTrash, FaDownload } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import CSVUploader from "../components/CSVUploader";
import LoadingSpinner from "../components/LoadingSpinner";
import { b2bCallsAPI } from "../services/api";
import "../styles/LeadManagement.css";

const PAGE_SIZE = 50;

const StatusBadge = ({ status }) => {
  const cfg =
    {
      not_called: { bg: "secondary", label: "not_called" },
      started: { bg: "primary", label: "started" },
      ongoing: { bg: "info", label: "ongoing" },
      completed: { bg: "success", label: "completed" },
      no_answer: { bg: "warning", label: "no_answer" },
      voicemail: { bg: "secondary", label: "voicemail" },
      failed: { bg: "danger", label: "failed" },
      invalid_number: { bg: "dark", label: "invalid_number" },

    }[status] || { bg: "secondary", label: status || "unknown" };

  return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
};

const InterestBadge = ({ interest }) => {
  const cfg =
    {
      yes: { bg: "success", label: "yes" },
      no: { bg: "danger", label: "no" },
      unclear: { bg: "warning", label: "unclear" },
    }[interest] || { bg: "secondary", label: interest || "unknown" };

  return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
};

const emptyLead = { name: "", service: "", phone_number: "", address: "" };
const formatLocation = (item) => {
  const parts = [
    item?.address?.trim(),
  ].filter(Boolean);

  return parts.length ? parts.join(", ") : "-";
};

const BatchCallModal = ({ show, onHide, batchLeadsQ, batchFilters, setBatchFilters, selectedLeads, setSelectedLeads, batchCallName, setBatchCallName, batchCallM, batchCallFilteredM, handleSelectLead, handleSelectAll, batchLeadsPage, setBatchLeadsPage, pager }) => {
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Call Leads</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Call Name</Form.Label>
              <Form.Control
                value={batchCallName}
                onChange={(e) => setBatchCallName(e.target.value)}
                placeholder="Enter Campaign name (optional)"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Filter by Call Status</Form.Label>
              <Form.Select value={batchFilters.call_status} onChange={(e) => setBatchFilters((p) => ({ ...p, call_status: e.target.value }))}>
                <option value="">All Leads</option>
                <option value="not_called">Not Called</option>
                <option value="no_answer">No Answer</option>
                <option value="voicemail">Voicemail</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {batchLeadsQ.isLoading ? (
          <LoadingSpinner />
        ) : batchLeadsQ.error ? (
          <Alert variant="danger">Error loading leads: {batchLeadsQ.error.message}</Alert>
        ) : (
          <>
            <div className="mb-3">
              <Button variant="outline-secondary" size="sm" onClick={handleSelectAll}>
                {selectedLeads.length === batchLeadsQ.data?.results?.length ? "Deselect All" : "Select All"}
              </Button>
              <span className="ms-2">Selected: {selectedLeads.length}</span>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {batchLeadsQ.data?.results?.length ? (
                batchLeadsQ.data.results.map((lead) => (
                  <Card key={lead.id} className="mb-3">
                    <Card.Body>
                      <Card.Header className="d-flex align-items-center">
                        <Form.Check
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          className="me-2"
                        />
                        <Card.Title className="mb-0">{lead.name}</Card.Title>
                      </Card.Header>
                      <Card.Text><strong>Phone:</strong> {lead.phone_number}</Card.Text>
                      <Card.Text><strong>Service:</strong> {lead.service || "-"}</Card.Text>
                      <Card.Text><strong>Location:</strong> {formatLocation(lead)}</Card.Text>
                      <Card.Text><strong>Call Status:</strong> <StatusBadge status={lead.call_status} /></Card.Text>
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <Alert variant="info" className="text-center">No leads found</Alert>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>
                        <Form.Check
                          checked={selectedLeads.length === batchLeadsQ.data?.results?.length && batchLeadsQ.data?.results?.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Service</th>
                      <th>Location</th>
                      <th>Call Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchLeadsQ.data?.results?.length ? (
                      batchLeadsQ.data.results.map((lead) => (
                        <tr key={lead.id}>
                          <td>
                            <Form.Check
                              checked={selectedLeads.includes(lead.id)}
                              onChange={() => handleSelectLead(lead.id)}
                            />
                          </td>
                          <td>{lead.name}</td>
                          <td>{lead.phone_number}</td>
                          <td>{lead.service || "-"}</td>
                          <td>{formatLocation(lead)}</td>
                          <td><StatusBadge status={lead.call_status} /></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No leads found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            {pager(batchLeadsQ.data, batchLeadsPage, setBatchLeadsPage)}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-column flex-sm-row">
        <Button variant="secondary" onClick={onHide} className="mb-2 mb-sm-0">
          Cancel
        </Button>
        <div className="d-flex flex-column flex-sm-row gap-2">
          <Button
            variant="warning"
            onClick={() => batchCallFilteredM.mutate({ filters: batchFilters, callName: batchCallName || `Campaign${Date.now()}` })}
            disabled={batchCallFilteredM.isPending}
          >
            {batchCallFilteredM.isPending ? <FaSpinner className="fa-spin me-2" /> : <FaPhone className="me-2" />}
            Call All Filtered
          </Button>
          <Button
            variant="success"
            onClick={() => batchCallM.mutate({ lead_ids: selectedLeads, call_name: batchCallName || `Campaign${Date.now()}` })}
            disabled={batchCallM.isPending || selectedLeads.length === 0}
          >
            {batchCallM.isPending ? <FaSpinner className="fa-spin me-2" /> : <FaPhone className="me-2" />}
            Start Batch Call (Selected)
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const CallDetailsModal = ({ show, onHide, call }) => {
  if (!call) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Call Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12} md={6}>
            <h5>Basic Information</h5>
            <p><strong>Company:</strong> {call.company_name || "-"}</p>
            <p><strong>Phone:</strong> {call.phone_number || "-"}</p>
            <p><strong>Status:</strong> <StatusBadge status={call.status} /></p>
            <p><strong>Created:</strong> {call.created_at ? new Date(call.created_at).toLocaleString() : "-"}</p>
          </Col>
          <Col xs={12} md={6}>
            <h5>Call Outcomes</h5>
            <p><strong>Interest:</strong> {call.interest ? <InterestBadge interest={call.interest} /> : "-"}</p>
            <p><strong>Address:</strong> {call.address }</p>
            <p><strong>Callback Requested:</strong> {call.callback_requested ? <Badge bg={call.callback_requested === "yes" ? "warning" : "secondary"}>{call.callback_requested}</Badge> : "-"}</p>
            <p><strong>Voicemail:</strong> {call.voicemail ? <Badge bg={call.voicemail === "yes" ? "warning" : "secondary"}>{call.voicemail}</Badge> : "-"}</p>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <h5>Notes</h5>
            <p>{call.notes || "No notes available"}</p>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <h5>Transcript</h5>
            <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #ccc", padding: "10px" }}>
              {call.transcript ? (
                <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{call.transcript}</pre>
              ) : (
                "No transcript available"
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const B2bCalls = () => {
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState("leads");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // applied search (triggers query)
  const [leadFilters, setLeadFilters] = useState({ call_status: "", qualified: "", service: "" });
  const [callFilters, setCallFilters] = useState({ status: "", interest: "", voicemail: "" });
  const [batchFilters, setBatchFilters] = useState({ call_status: "not_called", qualified: "", service: "" });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [batchCallName, setBatchCallName] = useState("");
  const [leadsPage, setLeadsPage] = useState(1);
  const [callsPage, setCallsPage] = useState(1);
  const [batchLeadsPage, setBatchLeadsPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [showCallDetails, setShowCallDetails] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [createForm, setCreateForm] = useState(emptyLead);
  const [editForm, setEditForm] = useState(emptyLead);

  // Reset page when filters/search/tab changes to avoid "empty page" confusion
  React.useEffect(() => setLeadsPage(1), [search, leadFilters]);
  React.useEffect(() => setCallsPage(1), [search, callFilters]);
  React.useEffect(() => {
    if (showBatch && !batchCallName) {
      setBatchCallName(`Campaign${Date.now()}`);
    }
  }, [showBatch, batchCallName]);
  const leadsKey = useMemo(() => ["b2bLeads", search, leadFilters, leadsPage], [search, leadFilters, leadsPage]);
  const callsKey = useMemo(() => ["b2bCalls", search, callFilters, callsPage], [search, callFilters, callsPage]);
  const batchLeadsKey = useMemo(() => ["batchLeads", batchFilters, batchLeadsPage], [batchFilters, batchLeadsPage]);

  // Queries
  const leadsQ = useQuery({
    queryKey: leadsKey,
    queryFn: () =>
      b2bCallsAPI.getLeads({
        q: search,
        ...leadFilters,
        page: leadsPage,
        page_size: PAGE_SIZE,
      }),
    keepPreviousData: true,
  });

  const callsQ = useQuery({
    queryKey: callsKey,
    queryFn: () =>
      b2bCallsAPI.getCalls({
        q: search,
        ...callFilters,
        page: callsPage,
        page_size: PAGE_SIZE,
      }),
    keepPreviousData: true,
  });

  const batchLeadsQ = useQuery({
    queryKey: batchLeadsKey,
    queryFn: () =>
      b2bCallsAPI.getLeads({
        ...batchFilters,
        page: batchLeadsPage,
        page_size: PAGE_SIZE,
      }),
    enabled: showBatch,
    keepPreviousData: true,
  });

  // Mutations
  const createLeadM = useMutation({
    mutationFn: b2bCallsAPI.createLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      setShowCreate(false);
      setCreateForm(emptyLead);
      toast.success("Lead created");
    },
    onError: (e) => toast.error(`Create failed: ${e?.message || "Unknown error"}`),
  });

  const updateLeadM = useMutation({
    mutationFn: ({ id, data }) => b2bCallsAPI.updateLead(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      setShowEdit(false);
      setEditingLead(null);
      toast.success("Lead updated");
    },
    onError: (e) => toast.error(`Update failed: ${e?.message || "Unknown error"}`),
  });

  const deleteLeadM = useMutation({
    mutationFn: (leadId) => b2bCallsAPI.deleteLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      toast.success("Lead deleted");
    },
    onError: (e) => toast.error(`Delete failed: ${e?.message || "Unknown error"}`),
  });

  const singleCallM = useMutation({
    mutationFn: b2bCallsAPI.makeSingleCall,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      queryClient.invalidateQueries({ queryKey: ["b2bCalls"] });
      toast.success("Call initiated");
    },
    onError: (e) => toast.error(`Call failed: ${e?.message || "Unknown error"}`),
  });

  const importLeadsM = useMutation({
    mutationFn: b2bCallsAPI.importLeads,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      toast.success("Leads imported");
    },
    onError: (e) => toast.error(`Import failed: ${e?.message || "Unknown error"}`),
  });

  const batchCallM = useMutation({
    mutationFn: b2bCallsAPI.makeBatchCall,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      queryClient.invalidateQueries({ queryKey: ["b2bCalls"] });
      setShowBatch(false);
      setSelectedLeads([]);
      setBatchCallName("");
      toast.success("Batch call initiated");
    },
    onError: (e) => toast.error(`Batch call failed: ${e?.message || "Unknown error"}`),
  });

  const batchCallFilteredM = useMutation({
    mutationFn: ({ filters, callName }) => b2bCallsAPI.makeBatchCallFiltered(filters, callName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2bLeads"] });
      queryClient.invalidateQueries({ queryKey: ["b2bCalls"] });
      setShowBatch(false);
      setSelectedLeads([]);
      setBatchCallName("");
      toast.success("Batch call initiated");
    },
    onError: (e) => toast.error(`Batch call failed: ${e?.message || "Unknown error"}`),
  });

  // Handlers
  const openEdit = (lead) => {
    setEditingLead(lead);
    setEditForm({
      name: lead?.name || "",
      service: lead?.service || "",
      phone_number: lead?.phone_number || "",
      address: lead?.address || "",
    });
    setShowEdit(true);
  };

  const openCallDetails = (call) => {
    setSelectedCall(call);
    setShowCallDetails(true);
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId) 
        : [...prev, leadId]
    );
  };

  const handleSingleCall = (leadId) => {
    singleCallM.mutate({ lead_id: leadId });
  };

  const handleDeleteLead = (leadId) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      deleteLeadM.mutate(leadId);
    }
  };

  const handleSelectAll = () => {
    if (batchLeadsQ.data?.results) {
      const allIds = batchLeadsQ.data.results.map(lead => lead.id);
      setSelectedLeads(prev => 
        prev.length === allIds.length ? [] : allIds
      );
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await b2bCallsAPI.exportB2bCallsCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "b2b_calls.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error(`Failed to export CSV: ${error.message}`);
    }
  };

  // Pagination helpers
  const pager = (data, page, setPage) => {
    if (!data || !data.total || data.total <= data.page_size) return null;
    return (
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
        <div className="text-center text-sm-start">
          Showing {(page - 1) * data.page_size + 1} to {Math.min(page * data.page_size, data.total)} of {data.total}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="align-self-center d-none d-sm-inline">
            Page {data.page} of {data.pages}
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={page >= data.pages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="lead-management">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <h2 className="m-0 mb-3 mb-md-0">B2B Calls Management</h2>
        <div className="d-flex flex-column flex-sm-row gap-2">
          <CSVUploader
            onUpload={(file) => {
              const fd = new FormData();
              fd.append("file", file);
              importLeadsM.mutate(fd);
            }}
          />
          <Button variant="outline-success" onClick={handleExportCSV}>
            <FaDownload className="me-2" />
            Export CSV
          </Button>
          <Button variant="success" onClick={() => setShowBatch(true)}>
            <FaPhone className="me-2" />
            Create B2B Campaign
          </Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <FaPlus className="me-2" />
            Create Lead
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Row className="mb-4">
        <Col xs={12} md={6} lg={4}>
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <Form.Control
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
              placeholder="Search by name, phone, address..."
            />
          </Form.Group>
        </Col>
        {activeTab === "leads" && (
          <>
            <Col xs={12} md={6} lg={2}>
              <Form.Group>
                <Form.Label>Call Status</Form.Label>
                <Form.Select value={leadFilters.call_status} onChange={(e) => setLeadFilters((p) => ({ ...p, call_status: e.target.value }))}>
                  <option value="">All</option>
                  <option value="not_called">Not Called</option>
                  <option value="completed">Completed</option>
                  <option value="no_answer">No Answer</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="failed">Failed</option>
                  <option value="invalid_number">Invalid Number</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={2}>
              <Form.Group>
                <Form.Label>Qualified</Form.Label>
                <Form.Select value={leadFilters.qualified} onChange={(e) => setLeadFilters((p) => ({ ...p, qualified: e.target.value }))}>
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={2}>
              <Form.Group>
                <Form.Label>Service</Form.Label>
                <Form.Control value={leadFilters.service} onChange={(e) => setLeadFilters((p) => ({ ...p, service: e.target.value }))} placeholder="Filter by service" />
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={2} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={() => setSearch(searchInput)} className="w-100">
                Apply Filters
              </Button>
            </Col>
          </>
        )}
        {activeTab === "calls" && (
          <>
            <Col xs={12} md={6} lg={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select value={callFilters.status} onChange={(e) => setCallFilters((p) => ({ ...p, status: e.target.value }))}>
                  <option value="">All</option>
                  <option value="completed">Completed</option>
                  <option value="no_answer">No Answer</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="failed">Failed</option>
                  <option value="invalid_number">Invalid Number</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={2}>
              <Form.Group>
                <Form.Label>Interest</Form.Label>
                <Form.Select value={callFilters.interest} onChange={(e) => setCallFilters((p) => ({ ...p, interest: e.target.value }))}>
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unclear">Unclear</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={2}>
              <Form.Group>
                <Form.Label>Voicemail</Form.Label>
                <Form.Select value={callFilters.voicemail} onChange={(e) => setCallFilters((p) => ({ ...p, voicemail: e.target.value }))}>
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={2} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={() => setSearch(searchInput)} className="w-100">
                Apply Filters
              </Button>
            </Col>
          </>
        )}
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "leads")} className="mb-4">
        <Tab eventKey="leads" title="B2B Leads">
          {leadsQ.isLoading ? (
            <LoadingSpinner />
          ) : leadsQ.error ? (
            <Alert variant="danger">Error loading leads: {leadsQ.error.message}</Alert>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="d-md-none">
                {leadsQ.data?.results?.map((lead) => (
                  <Card key={lead.id} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5>{lead.name || "Unnamed"}</h5>
                          <p className="mb-1">{lead.phone_number}</p>
                          <p className="mb-1">{formatLocation(lead)}</p>
                        </div>
                        <div>
                          <StatusBadge status={lead.call_status} />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleSingleCall(lead.id)}
                          disabled={singleCallM.isPending}
                          className="me-2"
                        >
                          <FaPhone />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openEdit(lead)}
                          className="me-2"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteLead(lead.id)}
                          disabled={deleteLeadM.isPending}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadsQ.data?.results?.map((lead) => (
                      <tr key={lead.id}>
                        <td>{lead.name || "Unnamed"}</td>
                        <td>{lead.phone_number}</td>
                        <td>{formatLocation(lead)}</td>
                        <td><StatusBadge status={lead.call_status} /></td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSingleCall(lead.id)}
                            disabled={singleCallM.isPending}
                            className="me-2"
                          >
                            <FaPhone />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => openEdit(lead)}
                            className="me-2"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteLead(lead.id)}
                            disabled={deleteLeadM.isPending}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {pager(leadsQ.data, leadsPage, setLeadsPage)}
            </>
          )}
        </Tab>

        <Tab eventKey="calls" title="B2B Calls">
          {callsQ.isLoading ? (
            <LoadingSpinner />
          ) : callsQ.error ? (
            <Alert variant="danger">Error loading calls: {callsQ.error.message}</Alert>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="d-md-none">
                {callsQ.data?.results?.map((call) => (
                  <Card key={call.id} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5>{call.lead?.name || "Unknown"}</h5>
                          <p className="mb-1">{call.lead?.phone_number}</p>
                          <p className="mb-1">{call.created_at}</p>
                        </div>
                        <div>
                          <StatusBadge status={call.status} />
                        </div>
                      </div>
                      {call.transcript && (
                        <div className="mt-3">
                          <strong>Transcript:</strong>
                          <p className="mb-0">{call.transcript}</p>
                        </div>
                      )}
                      <Button size="sm" variant="outline-info" onClick={() => openCallDetails(call)} title="Details">
                        Details
                      </Button>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Interest</th>
                      <th>Created</th>
                      <th>Transcript</th>
                      <th style={{ width: 80 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callsQ.data?.results?.map((call) => (
                      <tr key={call.id}>
                        <td>{call.lead?.name || "Unknown"}</td>
                        <td>{call.lead?.phone_number}</td>
                        <td><StatusBadge status={call.status} /></td>
                        <td><InterestBadge interest={call.interest} /></td>
                        <td>{call.created_at}</td>
                        <td>
                          {call.transcript ? (
                            <span title={call.transcript}>
                              {call.transcript.length > 50
                                ? `${call.transcript.substring(0, 50)}...`
                                : call.transcript}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <Button size="sm" variant="outline-info" onClick={() => openCallDetails(call)} title="Details">
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {pager(callsQ.data, callsPage, setCallsPage)}
            </>
          )}
        </Tab>
      </Tabs>

      {/* Create Lead Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={createForm.name}
                    onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Enter lead name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    value={createForm.phone_number}
                    onChange={(e) => setCreateForm((p) => ({ ...p, phone_number: e.target.value }))}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    value={createForm.address}
                    onChange={(e) => setCreateForm((p) => ({ ...p, address: e.target.value }))}
                    placeholder="Enter address"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service</Form.Label>
                  <Form.Control
                    value={createForm.service}
                    onChange={(e) => setCreateForm((p) => ({ ...p, service: e.target.value }))}
                    placeholder="Enter service"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => createLeadM.mutate(createForm)} disabled={createLeadM.isPending || !createForm?.name || !createForm?.phone_number}>
            {createLeadM.isPending ? <FaSpinner className="fa-spin" /> : null}
            Create Lead
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Lead Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Enter lead name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    value={editForm.phone_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone_number: e.target.value }))}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    value={editForm.address}
                    onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))}
                    placeholder="Enter address"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Service</Form.Label>
                  <Form.Control
                    value={editForm.service}
                    onChange={(e) => setEditForm((p) => ({ ...p, service: e.target.value }))}
                    placeholder="Enter service"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => updateLeadM.mutate({ id: editingLead?.id, data: editForm })} disabled={updateLeadM.isPending || !editingLead?.id}>
            {updateLeadM.isPending ? <FaSpinner className="fa-spin" /> : null}
            Update Lead
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Batch Call Modal */}
      <BatchCallModal
        show={showBatch}
        onHide={() => setShowBatch(false)}
        batchLeadsQ={batchLeadsQ}
        batchFilters={batchFilters}
        setBatchFilters={setBatchFilters}
        selectedLeads={selectedLeads}
        setSelectedLeads={setSelectedLeads}
        batchCallName={batchCallName}
        setBatchCallName={setBatchCallName}
        batchCallM={batchCallM}
        batchCallFilteredM={batchCallFilteredM}
        handleSelectLead={handleSelectLead}
        handleSelectAll={handleSelectAll}
        batchLeadsPage={batchLeadsPage}
        setBatchLeadsPage={setBatchLeadsPage}
        pager={pager}
      />

      {/* Call Details Modal */}
      <CallDetailsModal
        show={showCallDetails}
        onHide={() => setShowCallDetails(false)}
        call={selectedCall}
      />
    </Container>
  );
};

export default B2bCalls;